import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { cn } from '@workspace/ui/lib/utils'
import {
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  ChevronDown,
  FileText,
  Loader2,
  Shield,
  Sparkles,
  Target,
  TrendingUp,
} from 'lucide-react'
import { useState } from 'react'
import { MetricTooltip } from './metric-tooltip.js'
import { AI_SECTION_TOOLTIPS, PERCEPTION_TOOLTIPS } from './metric-tooltips.js'

// ─── Types matching backend schema v2 ────────────────────────────────

export interface AiReportFinding {
  type: 'positive' | 'warning' | 'opportunity'
  title: string
  description: string
}

export interface AiReportDimension {
  dimension: string
  status: 'strong' | 'moderate' | 'weak'
  summary: string
  topContent: string
  insight: string
}

export interface AiReportRisk {
  title: string
  engagement: 'high' | 'medium' | 'low'
  detail: string
}

export interface AiReportDocument {
  name: string
  pagesViewed: string
  hotspots: string
  dropoff: string
  recommendation: string
  pageHeatmap?: { page: number; seconds: number; viewers: number }[]
  documentId?: string
}

export interface AiReportRecommendation {
  priority: 'high' | 'medium' | 'low'
  action: string
  reason: string
}

export interface AiReportData {
  summary: string
  keyFindings: AiReportFinding[]
  detail: {
    strengthsWeaknesses: AiReportDimension[]
    riskPerception: {
      summary: string
      risks: AiReportRisk[]
    }
    contentEffectiveness: {
      summary: string
      documents: AiReportDocument[]
    }
    behaviorPatterns: {
      averageSession: string
      averageExpansions: string
      commonFlow: string
      returnVisitors: string
      returnVsFirst: string
      peakActivity: string
    }
    recommendations: AiReportRecommendation[]
  }
}

// ─── Config ──────────────────────────────────────────────────────────

const FINDING_CONFIG = {
  positive: {
    icon: CheckCircle2,
    color: 'text-emerald-400',
    bg: 'border-emerald-400/20 bg-emerald-400/[0.03]',
  },
  warning: {
    icon: AlertTriangle,
    color: 'text-amber-400',
    bg: 'border-amber-400/20 bg-amber-400/[0.03]',
  },
  opportunity: {
    icon: ArrowUpRight,
    color: 'text-purple-400',
    bg: 'border-purple-400/20 bg-purple-400/[0.03]',
  },
} as const

const STATUS_CONFIG = {
  strong: {
    label: 'Strong',
    color: 'bg-emerald-400/10 text-emerald-400',
    cardBorder: 'border-emerald-400/20',
  },
  moderate: {
    label: 'Moderate',
    color: 'bg-amber-400/10 text-amber-400',
    cardBorder: 'border-amber-400/20',
  },
  weak: { label: 'Weak', color: 'bg-red-400/10 text-red-400', cardBorder: 'border-red-400/20' },
} as const

const RISK_ENGAGEMENT = {
  high: { label: 'High concern', color: 'text-amber-400', badge: 'bg-amber-400/10 text-amber-400' },
  medium: {
    label: 'Some concern',
    color: 'text-muted-foreground',
    badge: 'bg-muted text-muted-foreground',
  },
  low: { label: 'Low concern', color: 'text-zinc-500', badge: 'bg-zinc-500/10 text-zinc-500' },
} as const

const PRIORITY_CONFIG = {
  high: { color: 'bg-red-400 text-white' },
  medium: { color: 'bg-amber-400 text-black' },
  low: { color: 'bg-zinc-500 text-white' },
} as const

// ─── Page engagement list ────────────────────────────────────────────

function PageEngagementList({
  heatmap,
}: {
  heatmap: { page: number; seconds: number; viewers: number }[]
}) {
  if (heatmap.length === 0) return null

  const maxSec = Math.max(...heatmap.map((h) => h.seconds), 1)
  const sorted = [...heatmap].sort((a, b) => b.seconds - a.seconds)

  return (
    <div className="mt-3">
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50">
        Pages viewed ({sorted.length})
      </p>
      <div className="space-y-1">
        {sorted.map((entry) => {
          const avgSec = entry.viewers > 0 ? Math.round(entry.seconds / entry.viewers) : 0
          const barWidth = Math.max((entry.seconds / maxSec) * 100, 4)
          return (
            <div key={entry.page} className="flex items-center gap-3">
              <span className="w-12 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                Page {entry.page}
              </span>
              <div className="flex-1">
                <div
                  className="h-4 rounded-sm"
                  style={{
                    width: `${barWidth}%`,
                    backgroundColor: `oklch(0.55 0.18 145 / ${Math.max((entry.seconds / maxSec) * 0.85, 0.2)})`,
                  }}
                />
              </div>
              <span className="w-20 shrink-0 text-right text-[11px] text-muted-foreground">
                {avgSec}s avg · {entry.viewers} viewer{entry.viewers !== 1 ? 's' : ''}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Empty / loading states ─────────────────────────────────────────

export function AiReportEmptyState() {
  return (
    <div className="rounded-xl border border-dashed border-border bg-card/20 px-6 py-10 text-center">
      <Sparkles className="mx-auto mb-3 size-8 text-[#f97316]/40" />
      <h3 className="text-sm font-semibold text-foreground">Engagement Insights</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Your AI-powered engagement report is not yet available. It will appear here once enough
        investor activity has been collected.
      </p>
    </div>
  )
}

export function AiReportLoading() {
  return (
    <div className="flex items-center justify-center gap-3 rounded-xl border border-border bg-card/20 py-12">
      <Loader2 className="size-5 animate-spin text-[#f97316]" />
      <p className="text-sm text-muted-foreground">Analyzing investor behavior patterns...</p>
    </div>
  )
}

// ─── Key Findings Cards ─────────────────────────────────────────────

export function KeyFindingsCards({ findings }: { findings: AiReportFinding[] }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      {findings.map((finding) => {
        const config = FINDING_CONFIG[finding.type]
        const Icon = config.icon
        return (
          <div key={finding.title} className={cn('rounded-xl border px-5 py-4', config.bg)}>
            <div className="mb-2 flex items-center gap-2">
              <Icon className={cn('size-4', config.color)} />
              <p className={cn('text-sm font-semibold', config.color)}>{finding.title}</p>
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground">{finding.description}</p>
          </div>
        )
      })}
    </div>
  )
}

// ─── Recommendations Panel ──────────────────────────────────────────

export function RecommendationsPanel({
  recommendations,
}: {
  recommendations: AiReportRecommendation[]
}) {
  return (
    <div className="rounded-xl bg-gradient-to-br from-[#f97316]/10 via-[#f97316]/5 to-transparent p-5">
      <div className="mb-4 flex items-center gap-2.5">
        <div className="flex size-8 items-center justify-center rounded-lg bg-[#f97316]/20">
          <Sparkles className="size-4 text-[#f97316]" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">Recommended Actions</p>
          <p className="text-[10px] text-muted-foreground">Based on aggregate investor behavior</p>
        </div>
      </div>
      <div className="space-y-2">
        {recommendations.map((rec) => {
          const pri = PRIORITY_CONFIG[rec.priority]
          const Icon =
            rec.priority === 'high'
              ? AlertTriangle
              : rec.priority === 'medium'
                ? Target
                : CheckCircle2
          return (
            <div
              key={rec.action}
              className="flex items-center gap-3 rounded-lg border border-[#f97316]/10 bg-black/20 px-4 py-3"
            >
              <Icon
                className={cn(
                  'size-4 shrink-0',
                  rec.priority === 'high'
                    ? 'text-red-400'
                    : rec.priority === 'medium'
                      ? 'text-amber-400'
                      : 'text-emerald-400'
                )}
              />
              <div className="flex-1">
                <p className="text-sm text-foreground">{rec.action}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{rec.reason}</p>
              </div>
              <span
                className={cn(
                  'shrink-0 rounded-md px-2 py-0.5 text-[9px] font-bold uppercase',
                  pri.color
                )}
              >
                {rec.priority}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Profile Perception (merged strengths + risks) ──────────────────

export function ProfilePerception({
  dimensions,
  risks,
}: {
  dimensions: AiReportDimension[]
  risks: { summary: string; risks: AiReportRisk[] }
}) {
  return (
    <div>
      <h4 className="mb-4 flex items-center gap-1.5 text-sm font-semibold text-foreground">
        Profile Perception
        <MetricTooltip text={AI_SECTION_TOOLTIPS['Profile Strengths & Weaknesses']!} />
      </h4>
      <div className="grid grid-cols-2 gap-4">
        {/* Left: Strengths & Weaknesses */}
        <div className="space-y-2">
          <p className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            Dimensions
            <MetricTooltip text={PERCEPTION_TOOLTIPS.Dimensions!} />
          </p>
          <div className="space-y-2">
            {dimensions.map((dim) => {
              const status = STATUS_CONFIG[dim.status]
              return (
                <div key={dim.dimension} className={cn('rounded-lg border p-3', status.cardBorder)}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">{dim.dimension}</span>
                    <span
                      className={cn(
                        'rounded-md px-2 py-0.5 text-[10px] font-semibold',
                        status.color
                      )}
                    >
                      {status.label}
                    </span>
                  </div>
                  <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                    {dim.insight}
                  </p>
                </div>
              )
            })}
          </div>
        </div>

        {/* Right: Risks */}
        <div className="space-y-2">
          <p className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            <Shield className="size-3" />
            Investor Risk Concerns
            <MetricTooltip text={PERCEPTION_TOOLTIPS['Investor Risk Concerns']!} />
          </p>
          {risks.risks.length > 0 ? (
            <div className="space-y-2">
              {risks.risks.map((risk) => {
                const eng = RISK_ENGAGEMENT[risk.engagement]
                return (
                  <div key={risk.title} className="rounded-lg border border-border p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground">{risk.title}</span>
                      <span
                        className={cn('rounded-md px-2 py-0.5 text-[10px] font-medium', eng.badge)}
                      >
                        {eng.label}
                      </span>
                    </div>
                    <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                      {risk.detail}
                    </p>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="py-4 text-xs text-muted-foreground">No risk engagement data yet.</p>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Content Effectiveness (compact) ────────────────────────────────

export function ContentEffectivenessCompact({
  content,
  onDocumentOpen,
}: {
  content: { summary: string; documents: AiReportDocument[] }
  onDocumentOpen?: (documentId: string) => void
}) {
  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <FileText className="size-4 text-emerald-400" />
        <h4 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
          Content Effectiveness
          <MetricTooltip text={AI_SECTION_TOOLTIPS['Content Effectiveness']!} />
        </h4>
      </div>
      {content.documents.length > 0 ? (
        <div className="overflow-hidden rounded-xl border border-border">
          {content.documents.map((doc, i) => (
            <ContentDocRow
              key={doc.name}
              doc={doc}
              isLast={i === content.documents.length - 1}
              onOpen={
                onDocumentOpen && doc.documentId ? () => onDocumentOpen(doc.documentId!) : undefined
              }
            />
          ))}
        </div>
      ) : (
        <p className="py-4 text-xs text-muted-foreground">No document engagement data yet.</p>
      )}
    </div>
  )
}

function ContentDocRow({
  doc,
  isLast,
  onOpen,
}: {
  doc: AiReportDocument
  isLast: boolean
  onOpen?: () => void
}) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className={cn(!isLast && 'border-b border-border')}>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: div needed to avoid nested button with Preview */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: expand/collapse row */}
      <div
        onClick={() => setExpanded(!expanded)}
        className="flex w-full cursor-pointer items-center gap-4 px-5 py-3 text-left transition-colors hover:bg-white/[0.02]"
      >
        <FileText className="size-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">{doc.name}</p>
        </div>
        <span className="shrink-0 text-xs text-muted-foreground/60">{doc.hotspots}</span>
        {onOpen && (
          <SecondaryButton
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onOpen()
            }}
          >
            Preview
          </SecondaryButton>
        )}
        <ChevronDown
          className={cn(
            'size-3.5 shrink-0 text-muted-foreground transition-transform',
            expanded && 'rotate-180'
          )}
        />
      </div>
      {expanded && (
        <div className="border-t border-border/50 bg-muted/[0.02] px-5 py-4">
          {doc.pageHeatmap && doc.pageHeatmap.length > 0 && (
            <PageEngagementList heatmap={doc.pageHeatmap} />
          )}
          <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50">
                Drop-off Point
              </p>
              <p className="mt-1 text-xs leading-relaxed text-foreground/70">{doc.dropoff}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50">
                Hotspot Pages
              </p>
              <p className="mt-1 text-xs leading-relaxed text-foreground/70">{doc.hotspots}</p>
            </div>
          </div>
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-[#f97316]/15 bg-[#f97316]/[0.03] px-4 py-3">
            <Sparkles className="mt-0.5 size-3.5 shrink-0 text-[#f97316]" />
            <p className="text-xs leading-relaxed text-foreground/80">{doc.recommendation}</p>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Behavior Patterns Grid ─────────────────────────────────────────

export function BehaviorPatternsGrid({
  patterns,
}: {
  patterns: AiReportData['detail']['behaviorPatterns']
}) {
  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <TrendingUp className="size-4 text-blue-400" />
        <h4 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
          Investor Behavior Patterns
          <MetricTooltip text={AI_SECTION_TOOLTIPS['Investor Behavior Patterns']!} />
        </h4>
      </div>
      <div className="grid grid-cols-4 gap-3">
        {[
          { value: patterns.averageSession, label: 'Avg. Session', color: 'text-blue-400' },
          {
            value: patterns.averageExpansions,
            label: 'Avg. Expansions',
            color: 'text-emerald-400',
          },
          { value: patterns.returnVisitors, label: 'Return Visitors', color: 'text-purple-400' },
          { value: patterns.peakActivity, label: 'Peak Activity', color: 'text-amber-400' },
        ].map((stat) => {
          const isEmpty = !stat.value || stat.value === '—'
          return (
            <div
              key={stat.label}
              className={cn(
                'rounded-xl border border-border/30 bg-muted/[0.04] px-4 py-3',
                isEmpty && 'opacity-50'
              )}
            >
              <p
                className={cn('text-lg font-bold', isEmpty ? 'text-muted-foreground' : stat.color)}
              >
                {isEmpty ? '—' : stat.value}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">{stat.label}</p>
            </div>
          )
        })}
      </div>
      {/* Journey moved to deterministic JourneyPathsSection in DeepInsightsTab */}
    </div>
  )
}

// ─── Legacy wrapper (kept for backward compat if used elsewhere) ────

export function AiReportSection({
  report,
  generatedAt,
  loading,
}: {
  report: AiReportData | null
  generatedAt: string | null
  loading: boolean
}) {
  if (!report && !loading) return <AiReportEmptyState />
  if (loading && !report) return <AiReportLoading />
  if (!report) return null

  const d = report.detail

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <Sparkles className="size-5 text-[#f97316]" />
        <h3 className="text-base font-semibold text-foreground">Engagement Insights</h3>
        {generatedAt && (
          <span className="text-xs text-muted-foreground">
            {new Date(generatedAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        )}
      </div>

      <KeyFindingsCards findings={report.keyFindings} />
      <ProfilePerception dimensions={d.strengthsWeaknesses} risks={d.riskPerception} />
      <ContentEffectivenessCompact content={d.contentEffectiveness} />
      <BehaviorPatternsGrid patterns={d.behaviorPatterns} />
      <RecommendationsPanel recommendations={d.recommendations} />
    </div>
  )
}
